import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Search, AlertTriangle, Heart, MapPin, Phone, Mail, 
  Dog, Cat, Bird, Rabbit, PawPrint, Plus, Filter,
  ArrowLeft, Clock, CheckCircle, Eye
} from "lucide-react";

const speciesIcons = { dog: Dog, cat: Cat, bird: Bird, rabbit: Rabbit, other: PawPrint };
const speciesLabels = { dog: "Cane", cat: "Gatto", bird: "Uccello", rabbit: "Coniglio", other: "Altro" };
const sizeLabels = { small: "Piccola", medium: "Media", large: "Grande" };

const statusConfig = {
  lost: { label: "Smarrito", color: "text-red-600", bg: "bg-red-50", icon: AlertTriangle },
  found: { label: "Trovato", color: "text-amber-600", bg: "bg-amber-50", icon: Eye },
  reunited: { label: "Ritrovato!", color: "text-emerald-600", bg: "bg-emerald-50", icon: Heart }
};

export default function LostPets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [myPets, setMyPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportType, setReportType] = useState("lost"); // lost or found
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filters
  const [filterSpecies, setFilterSpecies] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchCity, setSearchCity] = useState("");
  
  // Report form
  const [reportForm, setReportForm] = useState({
    pet_id: "",
    species: "dog",
    breed: "",
    name: "",
    color: "",
    size: "medium",
    description: "",
    last_seen_location: "",
    last_seen_date: new Date().toISOString().split('T')[0],
    photo_url: "",
    contact_phone: "",
    contact_email: "",
    microchip: "",
    reward: ""
  });

  useEffect(() => {
    fetchData();
  }, [filterSpecies, filterStatus, searchCity]);

  const fetchData = async () => {
    try {
      // Build query params
      let params = new URLSearchParams();
      if (filterSpecies !== "all") params.append("species", filterSpecies);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (searchCity) params.append("city", searchCity);
      
      const response = await fetch(`${API}/lost-pets/search?${params}`);
      if (response.ok) {
        setReports(await response.json());
      }
      
      // Get user's pets if logged in
      if (user) {
        const petsRes = await authFetch(`${API}/pets`);
        if (petsRes.ok) {
          setMyPets(await petsRes.json());
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const endpoint = reportType === "lost" 
        ? `${API}/lost-pets/report-lost`
        : `${API}/lost-pets/report-found`;
      
      const body = reportType === "lost" ? reportForm : {
        species: reportForm.species,
        breed: reportForm.breed,
        color: reportForm.color,
        size: reportForm.size,
        description: reportForm.description,
        found_location: reportForm.last_seen_location,
        found_date: reportForm.last_seen_date,
        photo_url: reportForm.photo_url,
        contact_phone: reportForm.contact_phone,
        contact_email: reportForm.contact_email,
        current_status: "with_finder"
      };
      
      const fetchFn = reportType === "lost" ? authFetch : fetch;
      const response = await fetchFn(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(reportType === "lost" 
          ? "Segnalazione smarrimento creata!" 
          : "Grazie per la segnalazione!");
        setShowReportDialog(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella segnalazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setReportForm({
      pet_id: "", species: "dog", breed: "", name: "", color: "", size: "medium",
      description: "", last_seen_location: "", last_seen_date: new Date().toISOString().split('T')[0],
      photo_url: "", contact_phone: "", contact_email: "", microchip: "", reward: ""
    });
  };

  const handlePetSelect = (petId) => {
    const pet = myPets.find(p => p.pet_id === petId);
    if (pet) {
      setReportForm(prev => ({
        ...prev,
        pet_id: petId,
        species: pet.species,
        breed: pet.breed || "",
        name: pet.name,
        microchip: pet.microchip || "",
        photo_url: pet.photo_url || ""
      }));
    }
  };

  const getDefaultImage = (species) => {
    const images = {
      dog: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200&q=80",
      cat: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=200&q=80",
      other: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=200&q=80"
    };
    return images[species] || images.other;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('it-IT', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse">
          <AlertTriangle size={48} className="text-red-500" />
        </div>
      </div>
    );
  }

  // Helper function for form fields - must be before desktop layout return
  const renderFormFields = () => (
    <>
      {/* Pet selection (only for lost) */}
      {reportType === 'lost' && myPets.length > 0 && (
        <div>
          <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
            Seleziona il tuo animale
          </Label>
          <Select onValueChange={handlePetSelect}>
            <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
              <SelectValue placeholder="Scegli dall'elenco..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Inserisci manualmente</SelectItem>
              {myPets.map(pet => (
                <SelectItem key={pet.pet_id} value={pet.pet_id}>
                  {pet.name} ({speciesLabels[pet.species]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Species */}
      <div>
        <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Specie *</Label>
        <Select value={reportForm.species} onValueChange={(v) => setReportForm({...reportForm, species: v})}>
          <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="dog">Cane</SelectItem>
            <SelectItem value="cat">Gatto</SelectItem>
            <SelectItem value="bird">Uccello</SelectItem>
            <SelectItem value="rabbit">Coniglio</SelectItem>
            <SelectItem value="other">Altro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Name (only for lost) */}
      {reportType === 'lost' && (
        <div>
          <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Nome</Label>
          <Input
            value={reportForm.name}
            onChange={(e) => setReportForm({...reportForm, name: e.target.value})}
            placeholder="Come si chiama?"
            className="h-11 rounded-xl bg-[#F5F5F7] border-0"
          />
        </div>
      )}

      {/* Color & Size */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Colore *</Label>
          <Input
            value={reportForm.color}
            onChange={(e) => setReportForm({...reportForm, color: e.target.value})}
            placeholder="Es. marrone, nero..."
            className="h-11 rounded-xl bg-[#F5F5F7] border-0"
            required
          />
        </div>
        <div>
          <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Taglia *</Label>
          <Select value={reportForm.size} onValueChange={(v) => setReportForm({...reportForm, size: v})}>
            <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">Piccola</SelectItem>
              <SelectItem value="medium">Media</SelectItem>
              <SelectItem value="large">Grande</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Descrizione *</Label>
        <Textarea
          value={reportForm.description}
          onChange={(e) => setReportForm({...reportForm, description: e.target.value})}
          placeholder="Segni particolari, comportamento, collare..."
          className="rounded-xl bg-[#F5F5F7] border-0 min-h-[80px]"
          required
        />
      </div>

      {/* Location & Date */}
      <div>
        <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
          {reportType === 'lost' ? 'Ultima posizione *' : 'Dove l\'hai trovato *'}
        </Label>
        <Input
          value={reportForm.last_seen_location}
          onChange={(e) => setReportForm({...reportForm, last_seen_location: e.target.value})}
          placeholder="Via, quartiere, città"
          className="h-11 rounded-xl bg-[#F5F5F7] border-0"
          required
        />
      </div>

      <div>
        <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Data *</Label>
        <Input
          type="date"
          value={reportForm.last_seen_date}
          onChange={(e) => setReportForm({...reportForm, last_seen_date: e.target.value})}
          className="h-11 rounded-xl bg-[#F5F5F7] border-0"
          required
        />
      </div>

      {/* Contact */}
      <div>
        <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Telefono di contatto *</Label>
        <Input
          value={reportForm.contact_phone}
          onChange={(e) => setReportForm({...reportForm, contact_phone: e.target.value})}
          placeholder="+39 333 1234567"
          className="h-11 rounded-xl bg-[#F5F5F7] border-0"
          required
        />
      </div>

      {/* Reward (only for lost) */}
      {reportType === 'lost' && (
        <div>
          <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Ricompensa (opzionale)</Label>
          <Input
            value={reportForm.reward}
            onChange={(e) => setReportForm({...reportForm, reward: e.target.value})}
            placeholder="Es. €100"
            className="h-11 rounded-xl bg-[#F5F5F7] border-0"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className={`w-full h-12 ${reportType === 'lost' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-white rounded-xl font-semibold mt-2`}
      >
        {isSubmitting ? "Invio..." : "Pubblica Segnalazione"}
      </Button>
    </>
  );

  // Desktop Layout
  if (!isMobile) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="lost-pets-page">
        <div className="max-w-6xl mx-auto p-8">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="w-10 h-10 rounded-xl bg-white border border-black/5 flex items-center justify-center hover:bg-[#F5F5F7] transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Animali Smarriti
                  </h1>
                  <p className="text-[#86868B]">Aiutaci a ritrovare i nostri amici a 4 zampe</p>
                </div>
              </div>
              <div className="flex gap-3">
                {user ? (
                  <Button
                    onClick={() => { setReportType("lost"); setShowReportDialog(true); }}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-xl h-11 px-6 font-medium"
                    data-testid="report-lost-btn"
                  >
                    <AlertTriangle size={18} className="mr-2" />
                    Ho perso il mio animale
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-red-500 hover:bg-red-600 text-white rounded-xl h-11 px-6"
                  >
                    Accedi per segnalare
                  </Button>
                )}
                <Button
                  onClick={() => { setReportType("found"); setShowReportDialog(true); }}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl h-11 px-6 font-medium"
                  data-testid="report-found-btn"
                >
                  <Eye size={18} className="mr-2" />
                  Ho trovato un animale
                </Button>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-4 gap-6">
            {/* Stats Cards */}
            <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
              <p className="text-4xl font-bold text-red-600">{reports.filter(r => r.status === 'lost').length}</p>
              <p className="text-red-600 font-medium">Smarriti</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
              <p className="text-4xl font-bold text-amber-600">{reports.filter(r => r.status === 'found').length}</p>
              <p className="text-amber-600 font-medium">Trovati</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
              <p className="text-4xl font-bold text-emerald-600">{reports.filter(r => r.status === 'reunited').length}</p>
              <p className="text-emerald-600 font-medium">Riuniti</p>
            </div>
            
            {/* Search & Filters */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  placeholder="Cerca per città..."
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="pl-10 h-10 rounded-xl bg-[#F5F5F7] border-0"
                />
              </div>
              <div className="flex gap-2">
                <Select value={filterSpecies} onValueChange={setFilterSpecies}>
                  <SelectTrigger className="flex-1 h-9 rounded-lg bg-[#F5F5F7] border-0 text-sm">
                    <SelectValue placeholder="Specie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="dog">Cani</SelectItem>
                    <SelectItem value="cat">Gatti</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="flex-1 h-9 rounded-lg bg-[#F5F5F7] border-0 text-sm">
                    <SelectValue placeholder="Stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="lost">Smarriti</SelectItem>
                    <SelectItem value="found">Trovati</SelectItem>
                    <SelectItem value="reunited">Riuniti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Results Grid */}
          <div className="mt-6">
            {reports.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-black/5">
                <Heart className="text-[#D1D1D6] mx-auto mb-4" size={64} />
                <h3 className="font-bold text-xl text-[#1D1D1F] mb-2">Nessuna segnalazione</h3>
                <p className="text-[#86868B]">
                  Non ci sono animali smarriti in questa zona
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {reports.map((report) => {
                  const status = statusConfig[report.status] || statusConfig.lost;
                  const StatusIcon = status.icon;
                  const SpeciesIcon = speciesIcons[report.species] || PawPrint;
                  
                  return (
                    <div
                      key={report.lost_pet_id}
                      className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className={`h-1.5 ${
                        report.status === 'lost' ? 'bg-red-500' :
                        report.status === 'found' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`} />
                      
                      <div className="p-5">
                        <div className="flex gap-4">
                          {/* Photo */}
                          <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[#F5F5F7]">
                            <img 
                              src={report.photo_url || getDefaultImage(report.species)}
                              alt={report.name || "Animale"}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-bold text-lg text-[#1D1D1F]">
                                  {report.name || `${speciesLabels[report.species]} ${report.color}`}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-[#86868B]">
                                  <SpeciesIcon size={14} />
                                  <span>{speciesLabels[report.species]}</span>
                                  {report.breed && <span>• {report.breed}</span>}
                                </div>
                              </div>
                              <span className={`${status.bg} ${status.color} px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-1`}>
                                <StatusIcon size={14} />
                                {status.label}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-sm text-[#6E6E73] mb-1">
                              <MapPin size={14} />
                              <span className="truncate">{report.last_seen_location}</span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-sm text-[#86868B]">
                              <Clock size={14} />
                              <span>{formatDate(report.last_seen_date)}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Description */}
                        {report.description && (
                          <p className="text-sm text-[#6E6E73] mt-3 line-clamp-2">
                            {report.description}
                          </p>
                        )}
                        
                        {/* Contact */}
                        <div className="flex gap-2 mt-4">
                          <a
                            href={`tel:${report.contact_phone}`}
                            className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B4A] text-white rounded-xl py-2.5 font-medium hover:bg-[#E55A3A] transition-colors"
                          >
                            <Phone size={16} />
                            Chiama
                          </a>
                          {report.contact_email && (
                            <a
                              href={`mailto:${report.contact_email}`}
                              className="flex items-center justify-center gap-2 bg-[#F5F5F7] text-[#1D1D1F] rounded-xl px-4 py-2.5 font-medium hover:bg-[#EBEBEB] transition-colors"
                            >
                              <Mail size={16} />
                            </a>
                          )}
                        </div>
                        
                        {/* Reward */}
                        {report.reward && (
                          <div className="mt-3 bg-amber-50 rounded-lg p-2 text-center">
                            <span className="text-amber-700 font-medium">
                              Ricompensa: {report.reward}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent className="max-w-[500px] max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-0 border-0">
            <div className="p-6">
              <DialogHeader className="mb-4">
                <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <div className={`w-10 h-10 rounded-xl ${reportType === 'lost' ? 'bg-red-500' : 'bg-amber-500'} flex items-center justify-center`}>
                    {reportType === 'lost' ? <AlertTriangle className="text-white" size={20} /> : <Eye className="text-white" size={20} />}
                  </div>
                  {reportType === 'lost' ? 'Segnala Smarrimento' : 'Segnala Ritrovamento'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmitReport} className="space-y-4">
                {renderFormFields()}
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Mobile Layout (original)
  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28 overflow-x-hidden max-w-full" data-testid="lost-pets-page">
      {/* Header - Simplified */}
      <div className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Animali Smarriti
            </h1>
            <p className="text-sm text-[#86868B]">Aiutaci a ritrovarli</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
            <p className="text-2xl font-bold text-red-600">{reports.filter(r => r.status === 'lost').length}</p>
            <p className="text-xs text-red-500">Smarriti</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
            <p className="text-2xl font-bold text-amber-600">{reports.filter(r => r.status === 'found').length}</p>
            <p className="text-xs text-amber-500">Trovati</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
            <p className="text-2xl font-bold text-emerald-600">{reports.filter(r => r.status === 'reunited').length}</p>
            <p className="text-xs text-emerald-500">Riuniti</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mb-4">
          {user ? (
            <Button
              onClick={() => { setReportType("lost"); setShowReportDialog(true); }}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-12 font-medium text-sm"
              data-testid="report-lost-btn"
            >
              <AlertTriangle size={16} className="mr-1.5" />
              Ho perso il mio animale
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/')}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-12 font-medium"
            >
              Accedi per segnalare
            </Button>
          )}
          <Button
            onClick={() => { setReportType("found"); setShowReportDialog(true); }}
            variant="outline"
            className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl h-12 font-medium text-sm"
            data-testid="report-found-btn"
          >
            <Eye size={16} className="mr-1.5" />
            Ho trovato un animale
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 mb-4">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
              <Input
                placeholder="Cerca per città..."
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="pl-10 h-10 rounded-xl bg-[#F5F5F7] border-0"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={filterSpecies} onValueChange={setFilterSpecies}>
              <SelectTrigger className="flex-1 h-9 rounded-lg bg-[#F5F5F7] border-0 text-sm">
                <SelectValue placeholder="Specie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le specie</SelectItem>
                <SelectItem value="dog">Cani</SelectItem>
                <SelectItem value="cat">Gatti</SelectItem>
                <SelectItem value="other">Altro</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="flex-1 h-9 rounded-lg bg-[#F5F5F7] border-0 text-sm">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="lost">Smarriti</SelectItem>
                <SelectItem value="found">Trovati</SelectItem>
                <SelectItem value="reunited">Riuniti</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        {reports.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-black/5">
            <Heart className="text-[#D1D1D6] mx-auto mb-4" size={48} />
            <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessuna segnalazione</h3>
            <p className="text-sm text-[#86868B]">
              Non ci sono animali smarriti in questa zona
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const status = statusConfig[report.status] || statusConfig.lost;
              const StatusIcon = status.icon;
              const SpeciesIcon = speciesIcons[report.species] || PawPrint;
              
              return (
                <div
                  key={report.lost_pet_id}
                  className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden"
                >
                  <div className={`h-1 ${
                    report.status === 'lost' ? 'bg-red-500' :
                    report.status === 'found' ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} />
                  
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Photo */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-[#F5F5F7]">
                        <img 
                          src={report.photo_url || getDefaultImage(report.species)}
                          alt={report.name || "Animale"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <h3 className="font-semibold text-[#1D1D1F]">
                              {report.name || `${speciesLabels[report.species]} ${report.color}`}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-[#86868B]">
                              <SpeciesIcon size={14} />
                              <span>{speciesLabels[report.species]}</span>
                              {report.breed && <span>• {report.breed}</span>}
                            </div>
                          </div>
                          <span className={`${status.bg} ${status.color} px-2 py-0.5 rounded-lg text-xs font-medium flex items-center gap-1`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-1 text-sm text-[#6E6E73] mb-2">
                          <MapPin size={14} />
                          <span className="truncate">{report.last_seen_location}</span>
                        </div>
                        
                        <div className="flex items-center gap-1 text-xs text-[#86868B]">
                          <Clock size={12} />
                          <span>{formatDate(report.last_seen_date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Description */}
                    {report.description && (
                      <p className="text-sm text-[#6E6E73] mt-3 line-clamp-2">
                        {report.description}
                      </p>
                    )}
                    
                    {/* Contact */}
                    <div className="flex gap-2 mt-3">
                      <a
                        href={`tel:${report.contact_phone}`}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B4A] text-white rounded-xl py-2.5 text-sm font-medium"
                      >
                        <Phone size={16} />
                        Chiama
                      </a>
                      {report.contact_email && (
                        <a
                          href={`mailto:${report.contact_email}`}
                          className="flex items-center justify-center gap-2 bg-[#F5F5F7] text-[#1D1D1F] rounded-xl px-4 py-2.5 text-sm font-medium"
                        >
                          <Mail size={16} />
                        </a>
                      )}
                    </div>
                    
                    {/* Reward */}
                    {report.reward && (
                      <div className="mt-3 bg-amber-50 rounded-lg p-2 text-center">
                        <span className="text-amber-700 text-sm font-medium">
                          🎁 Ricompensa: {report.reward}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-[420px] max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-0 border-0">
          <div className="p-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className={`w-10 h-10 rounded-xl ${reportType === 'lost' ? 'bg-red-500' : 'bg-amber-500'} flex items-center justify-center`}>
                  {reportType === 'lost' ? <AlertTriangle className="text-white" size={20} /> : <Eye className="text-white" size={20} />}
                </div>
                {reportType === 'lost' ? 'Segnala Smarrimento' : 'Segnala Ritrovamento'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmitReport} className="space-y-4">
              {/* Pet selection (only for lost) */}
              {reportType === 'lost' && myPets.length > 0 && (
                <div>
                  <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
                    Seleziona il tuo animale
                  </Label>
                  <Select onValueChange={handlePetSelect}>
                    <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
                      <SelectValue placeholder="Scegli dall'elenco..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Inserisci manualmente</SelectItem>
                      {myPets.map(pet => (
                        <SelectItem key={pet.pet_id} value={pet.pet_id}>
                          {pet.name} ({speciesLabels[pet.species]})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Species */}
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Specie *</Label>
                <Select value={reportForm.species} onValueChange={(v) => setReportForm({...reportForm, species: v})}>
                  <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Cane</SelectItem>
                    <SelectItem value="cat">Gatto</SelectItem>
                    <SelectItem value="bird">Uccello</SelectItem>
                    <SelectItem value="rabbit">Coniglio</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Name (only for lost) */}
              {reportType === 'lost' && (
                <div>
                  <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Nome</Label>
                  <Input
                    value={reportForm.name}
                    onChange={(e) => setReportForm({...reportForm, name: e.target.value})}
                    placeholder="Come si chiama?"
                    className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                  />
                </div>
              )}

              {/* Color & Size */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Colore *</Label>
                  <Input
                    value={reportForm.color}
                    onChange={(e) => setReportForm({...reportForm, color: e.target.value})}
                    placeholder="Es. marrone, nero..."
                    className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Taglia *</Label>
                  <Select value={reportForm.size} onValueChange={(v) => setReportForm({...reportForm, size: v})}>
                    <SelectTrigger className="h-11 rounded-xl bg-[#F5F5F7] border-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Piccola</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="large">Grande</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Descrizione *</Label>
                <Textarea
                  value={reportForm.description}
                  onChange={(e) => setReportForm({...reportForm, description: e.target.value})}
                  placeholder="Segni particolari, comportamento, collare..."
                  className="rounded-xl bg-[#F5F5F7] border-0 min-h-[80px]"
                  required
                />
              </div>

              {/* Location & Date */}
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">
                  {reportType === 'lost' ? 'Ultima posizione *' : 'Dove l\'hai trovato *'}
                </Label>
                <Input
                  value={reportForm.last_seen_location}
                  onChange={(e) => setReportForm({...reportForm, last_seen_location: e.target.value})}
                  placeholder="Via, quartiere, città"
                  className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                  required
                />
              </div>

              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Data *</Label>
                <Input
                  type="date"
                  value={reportForm.last_seen_date}
                  onChange={(e) => setReportForm({...reportForm, last_seen_date: e.target.value})}
                  className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                  required
                />
              </div>

              {/* Contact */}
              <div>
                <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Telefono di contatto *</Label>
                <Input
                  value={reportForm.contact_phone}
                  onChange={(e) => setReportForm({...reportForm, contact_phone: e.target.value})}
                  placeholder="+39 333 1234567"
                  className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                  required
                />
              </div>

              {/* Reward (only for lost) */}
              {reportType === 'lost' && (
                <div>
                  <Label className="text-[#1D1D1F] text-sm font-medium mb-2 block">Ricompensa (opzionale)</Label>
                  <Input
                    value={reportForm.reward}
                    onChange={(e) => setReportForm({...reportForm, reward: e.target.value})}
                    placeholder="Es. €100"
                    className="h-11 rounded-xl bg-[#F5F5F7] border-0"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full h-12 ${reportType === 'lost' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'} text-white rounded-xl font-semibold mt-2`}
              >
                {isSubmitting ? "Invio..." : "Pubblica Segnalazione"}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
